import React from 'react';
import { observer } from 'mobx-react-lite';

import { useI18next } from '@trader/services';

import { useMst } from '@trader/store';
import {
  balanceHistoryPositionsActions,
  formatAccountStatementDatetime,
  formatDecimal,
  getAccountStatementActionDescription,
  getCurrentDateTimeGMT,
} from '../utils';
import { Document, Page, Text, View } from '@react-pdf/renderer';
import { styles } from '../styles';
import { IAccountStatementHtmlReport } from './htmlReport';

export interface IAccountStatementPdfReport
  extends IAccountStatementHtmlReport {
  logo;
  balanceChart;
}

export const PDFReport: React.FC<IAccountStatementPdfReport> = observer(
  ({
    from,
    to,
    platformLogin,
    currency,
    product,
    accountType,
    accountStatement,
    logo,
    balanceChart,
  }) => {
    const { translate } = useI18next();
    const store = useMst();

    const TableData = ({
      label,
      value,
      viewStyles,
      labelStyles,
      valueStyles,
    }) => (
      <View style={viewStyles}>
        <Text style={labelStyles}>
          {label}
          {label === '' ? '' : ':'}
        </Text>
        <Text style={valueStyles}>{value}</Text>
      </View>
    );

    return (
      <Document style={styles.page}>
        <Page size='A4' style={styles.page}>
          <View style={styles.section}>
            <View style={styles.column}>
              <View style={styles.row}>
                <View style={styles.col9}>
                  <Text style={styles.h1}>
                    {translate('COMMON.LABELS.TRADE_HISTORY_REPORT')}
                  </Text>
                  <Text style={styles.b}>
                    {from} - {to}
                  </Text>
                </View>
                <View style={styles.col3}>
                  <View style={styles.column_MT_10}>{logo}</View>
                </View>
              </View>
              <View style={styles.column_MT_30}>
                <View style={styles.row}>
                  <View style={styles.col9}>
                    <TableData
                      viewStyles={styles.tr}
                      labelStyles={styles.label50}
                      valueStyles={styles.value}
                      label={translate('COMMON.LABELS.NAME')}
                      value={`${store.user.profile.firstName} ${store.user.profile.lastName}`}
                    />
                    <TableData
                      viewStyles={styles.tr}
                      labelStyles={styles.label50}
                      valueStyles={styles.value}
                      label={translate('COMMON.LABELS.ACCOUNT')}
                      value={`${platformLogin} (${currency}, ${product}, ${accountType})`}
                    />
                    <TableData
                      viewStyles={styles.tr}
                      labelStyles={styles.label50}
                      valueStyles={styles.value}
                      label={translate('COMMON.LABELS.COMPANY')}
                      value={store.app.brand}
                    />
                    <TableData
                      viewStyles={styles.tr}
                      labelStyles={styles.label50}
                      valueStyles={styles.value}
                      label={translate('COMMON.LABELS.DATE')}
                      value={getCurrentDateTimeGMT()}
                    />
                  </View>
                </View>
              </View>
              <View style={styles.column_MT_50}>
                <Text style={styles.h2}>
                  {translate('COMMON.LABELS.SUMMARY')}
                </Text>
              </View>
              <View style={styles.column_MT_5}>
                <View style={styles.tr}>
                  <TableData
                    viewStyles={styles.col4}
                    labelStyles={styles.label}
                    valueStyles={styles.value}
                    label={translate('COMMON.LABELS.BALANCE')}
                    value={formatDecimal(accountStatement.summary.balance)}
                  />
                  <TableData
                    viewStyles={styles.col4}
                    labelStyles={styles.label}
                    valueStyles={styles.value}
                    label={translate('COMMON.LABELS.USED_MARGIN')}
                    value={formatDecimal(accountStatement.summary.usedMargin)}
                  />
                  <TableData
                    viewStyles={styles.col4}
                    labelStyles={styles.label}
                    valueStyles={styles.value}
                    label={translate('COMMON.LABELS.DEPOSIT')}
                    value={formatDecimal(accountStatement.summary.deposit)}
                  />
                </View>
                <View style={styles.tr}>
                  <TableData
                    viewStyles={styles.col4}
                    labelStyles={styles.label}
                    valueStyles={styles.value}
                    label={translate('COMMON.LABELS.EQUITY')}
                    value={formatDecimal(accountStatement.summary.equity)}
                  />
                  <TableData
                    viewStyles={styles.col4}
                    labelStyles={styles.label}
                    valueStyles={styles.value}
                    label={translate('COMMON.LABELS.USABLE_MARGIN')}
                    value={formatDecimal(
                      accountStatement.summary.marginFree?.value
                    )}
                  />
                  <TableData
                    viewStyles={styles.col4}
                    labelStyles={styles.label}
                    valueStyles={styles.value}
                    label={translate('COMMON.LABELS.WITHDRAWAL')}
                    value={formatDecimal(accountStatement.summary.withdrawal)}
                  />
                </View>
                <View style={styles.tr}>
                  <TableData
                    viewStyles={styles.col4}
                    labelStyles={styles.label}
                    valueStyles={styles.value}
                    label={translate('COMMON.LABELS.NET_PL')}
                    value={formatDecimal(accountStatement.summary.grossPl)}
                  />
                  <TableData
                    viewStyles={styles.col4}
                    labelStyles={styles.label}
                    valueStyles={styles.value}
                    label={`${translate('COMMON.LABELS.USABLE_MARGIN')}, %`}
                    value={`${formatDecimal(
                      accountStatement.summary.marginFree?.percent
                    )}%`}
                  />
                  <TableData
                    viewStyles={styles.col4}
                    labelStyles={styles.label}
                    valueStyles={styles.value}
                    label={translate('COMMON.LABELS.ADJUSTMENT')}
                    value={formatDecimal(accountStatement.summary.adjustment)}
                  />
                </View>
                <View style={styles.tr}>
                  <TableData
                    viewStyles={styles.col4}
                    labelStyles={styles.label}
                    valueStyles={styles.value}
                    label={translate('COMMON.LABELS.REALIZED_TOTAL_PL')}
                    value={formatDecimal(accountStatement.summary.realizedPl)}
                  />
                  <TableData
                    viewStyles={styles.col4}
                    labelStyles={styles.label}
                    valueStyles={styles.value}
                    label={`${translate('COMMON.LABELS.EQUITY_MC_LEVEL')}, %`}
                    value={`${formatDecimal(
                      accountStatement.summary.mcLevel
                    )}%`}
                  />
                  <TableData
                    viewStyles={styles.col4}
                    labelStyles={styles.label}
                    valueStyles={styles.value}
                    label={translate('COMMON.LABELS.NET')}
                    value={formatDecimal(accountStatement.summary.net)}
                  />
                </View>
              </View>
              <View style={styles.column_MT_50}>
                <Text style={styles.h2}>
                  {translate('COMMON.LABELS.COSTS_OVERVIEW')}
                </Text>
              </View>
              <View style={styles.column_MT_10}>
                <Text style={styles.h3}>
                  {translate('COMMON.LABELS.TRADING_COSTS')}
                </Text>
              </View>
              <View style={styles.column_MT_5}>
                <View style={styles.col11}>
                  <View style={styles.row}>
                    <View style={styles.col3}>
                      <Text></Text>
                    </View>
                    <View style={styles.col3}>
                      <Text style={styles.bRight}>
                        {translate('COMMON.LABELS.ACTIVE_TRADING_COSTS')}
                      </Text>
                    </View>
                    <View style={styles.col3}>
                      <Text style={styles.bRight}>
                        {translate('COMMON.LABELS.CLOSED_TRADING_COSTS')}
                      </Text>
                    </View>
                    <View style={styles.col3}>
                      <Text style={styles.bRight}>
                        {translate('COMMON.LABELS.TOTAL_TRADING_COSTS')}
                      </Text>
                    </View>
                  </View>
                  <View style={styles.rowBB1}></View>
                  <View style={styles.rowMB5}>
                    <View style={styles.col3}>
                      <Text style={styles.b}>
                        {translate('COMMON.LABELS.OPEN_COMMISSIONS')}
                      </Text>
                    </View>
                    <View style={styles.col3}>
                      <Text style={styles.spanRight}>
                        {formatDecimal(
                          accountStatement.costsOverview?.activeTrading
                            ?.openCommissions
                        )}
                      </Text>
                    </View>
                    <View style={styles.col3}>
                      <Text style={styles.spanRight}>
                        {formatDecimal(
                          accountStatement.costsOverview?.closeTrading
                            ?.openCommissions
                        )}
                      </Text>
                    </View>
                    <View style={styles.col3}>
                      <Text style={styles.spanRight}>
                        {formatDecimal(
                          accountStatement.costsOverview?.totalTrading
                            ?.openCommissions
                        )}
                      </Text>
                    </View>
                  </View>
                  <View style={styles.rowMB5}>
                    <View style={styles.col3}>
                      <Text style={styles.b}>
                        {translate('COMMON.LABELS.CLOSE_COMMISSIONS')}
                      </Text>
                    </View>
                    <View style={styles.col3}>
                      <Text style={styles.spanRight}>
                        {formatDecimal(
                          accountStatement.costsOverview?.activeTrading
                            ?.closeCommissions
                        )}
                      </Text>
                    </View>
                    <View style={styles.col3}>
                      <Text style={styles.spanRight}>
                        {formatDecimal(
                          accountStatement.costsOverview?.closeTrading
                            ?.closeCommissions
                        )}
                      </Text>
                    </View>
                    <View style={styles.col3}>
                      <Text style={styles.spanRight}>
                        {formatDecimal(
                          accountStatement.costsOverview?.totalTrading
                            ?.closeCommissions
                        )}
                      </Text>
                    </View>
                  </View>
                  <View style={styles.row}>
                    <View style={styles.col3}>
                      <Text style={styles.b}>
                        {translate('COMMON.LABELS.SPREAD')}
                      </Text>
                    </View>
                    <View style={styles.col3}>
                      <Text style={styles.spanRight}>
                        {formatDecimal(
                          accountStatement.costsOverview?.activeTrading?.spread
                        )}
                      </Text>
                    </View>
                    <View style={styles.col3}>
                      <Text style={styles.spanRight}>
                        {formatDecimal(
                          accountStatement.costsOverview?.closeTrading?.spread
                        )}
                      </Text>
                    </View>
                    <View style={styles.col3}>
                      <Text style={styles.spanRight}>
                        {formatDecimal(
                          accountStatement.costsOverview?.totalTrading?.spread
                        )}
                      </Text>
                    </View>
                  </View>
                </View>
              </View>
              <View style={styles.column_MT_20}>
                <Text style={styles.h3}>
                  {translate('COMMON.LABELS.OTHER_ADJUSTMENTS')}
                </Text>
              </View>
              <View style={styles.column_MT_5}>
                <View style={styles.col11}>
                  <View style={styles.row}>
                    <View style={styles.col3}>
                      <Text></Text>
                    </View>
                    <View style={styles.col3}>
                      <Text style={styles.bRight}>
                        {translate('COMMON.LABELS.ACTIVE_OTHER_ADJUSTMENTS')}
                      </Text>
                    </View>
                    <View style={styles.col3}>
                      <Text style={styles.bRight}>
                        {translate('COMMON.LABELS.CLOSED_OTHER_ADJUSTMENTS')}
                      </Text>
                    </View>
                    <View style={styles.col3}>
                      <Text style={styles.bRight}>
                        {translate('COMMON.LABELS.TOTAL_OTHER_ADJUSTMENTS')}
                      </Text>
                    </View>
                  </View>
                  <View style={styles.rowBB1}></View>
                  <View style={styles.rowMB5}>
                    <View style={styles.col3}>
                      <Text style={styles.b}>
                        {translate('COMMON.LABELS.OVERNIGHT_SWAP')}
                      </Text>
                    </View>
                    <View style={styles.col3}>
                      <Text style={styles.spanRight}>
                        {formatDecimal(
                          accountStatement.costsOverview?.activeAdjustments
                            ?.swap
                        )}
                      </Text>
                    </View>
                    <View style={styles.col3}>
                      <Text style={styles.spanRight}>
                        {formatDecimal(
                          accountStatement.costsOverview?.closeAdjustments?.swap
                        )}
                      </Text>
                    </View>
                    <View style={styles.col3}>
                      <Text style={styles.spanRight}>
                        {formatDecimal(
                          accountStatement.costsOverview?.totalAdjustments?.swap
                        )}
                      </Text>
                    </View>
                  </View>
                  <View style={styles.rowMB5}>
                    <View style={styles.col3}>
                      <Text style={styles.b}>
                        {translate('COMMON.LABELS.DIVIDENDS')}
                      </Text>
                    </View>
                    <View style={styles.col3}>
                      <Text style={styles.spanRight}>
                        {formatDecimal(
                          accountStatement.costsOverview?.activeAdjustments
                            ?.dividends
                        )}
                      </Text>
                    </View>
                    <View style={styles.col3}>
                      <Text style={styles.spanRight}>
                        {formatDecimal(
                          accountStatement.costsOverview?.closeAdjustments
                            ?.dividends
                        )}
                      </Text>
                    </View>
                    <View style={styles.col3}>
                      <Text style={styles.spanRight}>
                        {formatDecimal(
                          accountStatement.costsOverview?.totalAdjustments
                            ?.dividends
                        )}
                      </Text>
                    </View>
                  </View>
                  <View style={styles.row}>
                    <View style={styles.col3}>
                      <Text style={styles.b}>
                        {translate('COMMON.LABELS.WHT')}
                      </Text>
                    </View>
                    <View style={styles.col3}>
                      <Text style={styles.spanRight}>
                        {formatDecimal(
                          accountStatement.costsOverview?.activeAdjustments?.wht
                        )}
                      </Text>
                    </View>
                    <View style={styles.col3}>
                      <Text style={styles.spanRight}>
                        {formatDecimal(
                          accountStatement.costsOverview?.closeAdjustments?.wht
                        )}
                      </Text>
                    </View>
                    <View style={styles.col3}>
                      <Text style={styles.spanRight}>
                        {formatDecimal(
                          accountStatement.costsOverview?.totalAdjustments?.wht
                        )}
                      </Text>
                    </View>
                  </View>
                </View>
              </View>
              {balanceChart !== null && (
                <View style={styles.column}>
                  <View style={styles.column_MT_50}>
                    <Text style={styles.h2}>
                      {translate('COMMON.LABELS.BALANCE_CHART')}
                    </Text>
                  </View>
                  <View style={styles.row}>
                    <View style={styles.chartBalanceArea}>
                      <Text style={styles.chartBalance}>
                        {translate('COMMON.LABELS.BALANCE')}
                      </Text>
                      <View style={styles.column_MT_5}>{balanceChart}</View>
                    </View>
                  </View>
                  <View style={styles.row}>
                    <Text style={styles.chartTransactions}>
                      {translate('COMMON.LABELS.TRANSACTIONS')}
                    </Text>
                  </View>
                </View>
              )}
              <View style={styles.column_MT_50}>
                <Text style={styles.h2}>
                  {translate('COMMON.LABELS.POSITIONS')}
                </Text>
              </View>
              <View style={styles.column_MT_5}>
                {accountStatement.openPositions.length > 0 ? (
                  <View style={styles.column}>
                    <View style={styles.rowMB5}>
                      <View style={styles.colPositions}>
                        <View style={styles.row}>
                          <Text style={styles.b8}>
                            {translate('COMMON.LABELS.POSITION_ID')}
                          </Text>
                        </View>
                      </View>
                      <View style={styles.col9_100}>
                        <View style={styles.rowCenter}>
                          <Text style={styles.b8}>
                            {translate('COMMON.LABELS.ENTRY_TIME')}
                          </Text>
                        </View>
                      </View>
                      <View style={styles.col8_100}>
                        <View style={styles.rowCenter}>
                          <Text style={styles.b8}>
                            {translate('COMMON.LABELS.SYMBOL')}
                          </Text>
                        </View>
                      </View>
                      <View style={styles.colPositions}>
                        <View style={styles.rowCenter}>
                          <Text style={styles.b8}>
                            {translate('COMMON.LABELS.DIRECTION')}
                          </Text>
                        </View>
                      </View>
                      <View style={styles.colPositions}>
                        <View style={styles.rowCenter}>
                          <Text style={styles.b8}>
                            {translate('COMMON.LABELS.QUANTITY')}
                          </Text>
                        </View>
                      </View>
                      <View style={styles.colPositions}>
                        <View style={styles.rowCenter}>
                          <Text style={styles.b8}>
                            {translate('COMMON.LABELS.ENTRY_PRICE')}
                          </Text>
                        </View>
                      </View>
                      <View style={styles.col6_100}>
                        <View style={styles.rowCenter}>
                          <Text style={styles.b8}>
                            {translate('COMMON.LABELS.STOP_LOSS')}
                          </Text>
                        </View>
                      </View>
                      <View style={styles.col6_100}>
                        <View style={styles.rowCenter}>
                          <Text style={styles.b8}>
                            {translate('COMMON.LABELS.TAKE_PROFIT')}
                          </Text>
                        </View>
                      </View>
                      <View style={styles.col6_100}>
                        <View style={styles.rowCenter}>
                          <Text style={styles.b8}>
                            {translate('COMMON.LABELS.SWAP')}
                          </Text>
                        </View>
                      </View>
                      <View style={styles.colPositions}>
                        <View style={styles.rowCenter}>
                          <Text style={styles.b8}>
                            {translate('COMMON.LABELS.OPEN_COMMISSION')}
                          </Text>
                        </View>
                      </View>
                      <View style={styles.colPositions}>
                        <View style={styles.rowCenter}>
                          <Text style={styles.b8}>
                            {translate('COMMON.LABELS.CLOSE_COMMISSION')}
                          </Text>
                        </View>
                      </View>
                      <View style={styles.colPositions}>
                        <View style={styles.rowCenter}>
                          <Text style={styles.b8}>
                            {translate('COMMON.LABELS.PRICE')}
                          </Text>
                        </View>
                      </View>
                      <View style={styles.colPositions}>
                        <View style={styles.rowCenter}>
                          <Text style={styles.b8}>
                            {translate('COMMON.LABELS.FEE')}
                          </Text>
                        </View>
                      </View>
                      <View style={styles.colPositions}>
                        <View style={styles.rowCenter}>
                          <Text style={styles.b8}>
                            {translate('COMMON.LABELS.SPREAD')}
                          </Text>
                        </View>
                      </View>
                      <View style={styles.colPositions}>
                        <View style={styles.rowCenter}>
                          <Text style={styles.b8}>
                            {translate('COMMON.LABELS.NET_P')}
                          </Text>
                        </View>
                      </View>
                      <View style={styles.colPositions}>
                        <View style={styles.rowCenter}>
                          <Text style={styles.b8}>
                            {translate('COMMON.LABELS.COMMENT')}
                          </Text>
                        </View>
                      </View>
                    </View>
                    {accountStatement.openPositions.map(t => (
                      <View style={styles.rowMB5}>
                        <View style={styles.colPositions}>
                          <View style={styles.row}>
                            <Text style={styles.span8}>{t.positionId}</Text>
                          </View>
                        </View>
                        <View style={styles.col9_100}>
                          <View style={styles.rowCenter}>
                            <Text style={styles.span8}>
                              {formatAccountStatementDatetime(t.entryTime)}
                            </Text>
                          </View>
                        </View>
                        <View style={styles.col8_100}>
                          <View style={styles.rowCenter}>
                            <Text style={styles.span8}>{t.symbol}</Text>
                          </View>
                        </View>
                        <View style={styles.colPositions}>
                          <View style={styles.rowCenter}>
                            <Text style={styles.span8}>{t.direction}</Text>
                          </View>
                        </View>
                        <View style={styles.colPositions}>
                          <View style={styles.rowCenter}>
                            <Text style={styles.span8}>{t.quantity}</Text>
                          </View>
                        </View>
                        <View style={styles.colPositions}>
                          <View style={styles.rowCenter}>
                            <Text style={styles.span8}>{t.entryPrice}</Text>
                          </View>
                        </View>
                        <View style={styles.col6_100}>
                          <View style={styles.rowCenter}>
                            <Text style={styles.span8}>{t.stopLoss}</Text>
                          </View>
                        </View>
                        <View style={styles.col6_100}>
                          <View style={styles.rowCenter}>
                            <Text style={styles.span8}>{t.takeProfit}</Text>
                          </View>
                        </View>
                        <View style={styles.col6_100}>
                          <View style={styles.rowCenter}>
                            <Text style={styles.span8}>{t.swap}</Text>
                          </View>
                        </View>
                        <View style={styles.colPositions}>
                          <View style={styles.rowCenter}>
                            <Text style={styles.span8}>{t.openCommission}</Text>
                          </View>
                        </View>
                        <View style={styles.colPositions}>
                          <View style={styles.rowCenter}>
                            <Text style={styles.span8}>
                              {t.closeCommission}
                            </Text>
                          </View>
                        </View>
                        <View style={styles.colPositions}>
                          <View style={styles.rowCenter}>
                            <Text style={styles.span8}>{t.price}</Text>
                          </View>
                        </View>
                        <View style={styles.colPositions}>
                          <View style={styles.rowCenter}>
                            <Text style={styles.span8}>{t.fee}</Text>
                          </View>
                        </View>
                        <View style={styles.colPositions}>
                          <View style={styles.rowCenter}>
                            <Text style={styles.span8}>{t.spread}</Text>
                          </View>
                        </View>
                        <View style={styles.colPositions}>
                          <View style={styles.rowCenter}>
                            <Text style={styles.span8}>{t.pl}</Text>
                          </View>
                        </View>
                        <View style={styles.colPositions}>
                          <View style={styles.rowCenter}>
                            <Text style={styles.span8}>{t.comment}</Text>
                          </View>
                        </View>
                      </View>
                    ))}
                  </View>
                ) : (
                  <Text style={styles.span}>
                    {translate('COMMON.LABELS.NO_TRANSACTIONS')}
                  </Text>
                )}
              </View>
              <View style={styles.column_MT_50}>
                <Text style={styles.h2}>
                  {translate('COMMON.LABELS.DEALS')}
                </Text>
              </View>
              <View style={styles.column_MT_5}>
                {accountStatement.balanceHistory.length > 0 ? (
                  <View style={styles.column}>
                    <View style={styles.rowMB5}>
                      <View style={styles.colPositions}>
                        <View style={styles.row}>
                          <Text style={styles.b8}>
                            {translate('COMMON.LABELS.POSITION_ID')}
                          </Text>
                        </View>
                      </View>
                      <View style={styles.col}>
                        <View style={styles.rowCenter}>
                          <Text style={styles.b8}>
                            {translate('COMMON.LABELS.ENTRY_TIME')}
                          </Text>
                        </View>
                      </View>
                      <View style={styles.col}>
                        <View style={styles.rowCenter}>
                          <Text style={styles.b8}>
                            {translate('COMMON.LABELS.EXIT_TRANSFER_TIME')}
                          </Text>
                        </View>
                      </View>
                      <View style={styles.colPositions}>
                        <View style={styles.rowCenter}>
                          <Text style={styles.b8}>
                            {translate('COMMON.LABELS.SYMBOL')}
                          </Text>
                        </View>
                      </View>
                      <View style={styles.col}>
                        <View style={styles.rowCenter}>
                          <Text style={styles.b8}>
                            {translate('COMMON.LABELS.DIRECTION')}
                          </Text>
                        </View>
                      </View>
                      <View style={styles.colPositions}>
                        <View style={styles.rowCenter}>
                          <Text style={styles.b8}>
                            {translate('COMMON.LABELS.QUANTITY')}
                          </Text>
                        </View>
                      </View>
                      <View style={styles.col}>
                        <View style={styles.rowCenter}>
                          <Text style={styles.b8}>
                            {translate('COMMON.LABELS.ENTRY_PRICE')}
                          </Text>
                        </View>
                      </View>
                      <View style={styles.col}>
                        <View style={styles.rowCenter}>
                          <Text style={styles.b8}>
                            {translate('COMMON.LABELS.EXIT_PRICE')}
                          </Text>
                        </View>
                      </View>
                      <View style={styles.col}>
                        <View style={styles.rowCenter}>
                          <Text style={styles.b8}>
                            {translate('COMMON.LABELS.FEE')}
                          </Text>
                        </View>
                      </View>
                      <View style={styles.col}>
                        <View style={styles.rowCenter}>
                          <Text style={styles.b8}>
                            {translate('COMMON.LABELS.COMMISSION')}
                          </Text>
                        </View>
                      </View>
                      <View style={styles.col}>
                        <View style={styles.rowCenter}>
                          <Text style={styles.b8}>
                            {translate('COMMON.LABELS.SWAP')}
                          </Text>
                        </View>
                      </View>
                      <View style={styles.col}>
                        <View style={styles.rowCenter}>
                          <Text style={styles.b8}>
                            {translate('COMMON.LABELS.SPREAD')}
                          </Text>
                        </View>
                      </View>
                      <View style={styles.col}>
                        <View style={styles.rowCenter}>
                          <Text style={styles.b8}>
                            {translate('COMMON.LABELS.PROFIT_LOSS_BALANCE')}
                          </Text>
                        </View>
                      </View>
                      <View style={styles.col}>
                        <View style={styles.rowCenter}>
                          <Text style={styles.b8}>
                            {translate('COMMON.LABELS.COMMENT')}
                          </Text>
                        </View>
                      </View>
                    </View>
                    {accountStatement.balanceHistory.map(t => (
                      <View style={styles.row}>
                        {balanceHistoryPositionsActions.includes(t.action) ? (
                          <View style={styles.rowMB5}>
                            <View style={styles.colPositions}>
                              <View style={styles.row}>
                                <Text style={styles.span8}>{t.positionId}</Text>
                              </View>
                            </View>
                            <View style={styles.col}>
                              <View style={styles.rowCenter}>
                                <Text style={styles.span8}>
                                  {formatAccountStatementDatetime(t.entryTime)}
                                </Text>
                              </View>
                            </View>
                            <View style={styles.col}>
                              <View style={styles.rowCenter}>
                                <Text style={styles.span8}>
                                  {formatAccountStatementDatetime(t.exitTime)}
                                </Text>
                              </View>
                            </View>
                            <View style={styles.colPositions}>
                              <View style={styles.rowCenter}>
                                <Text style={styles.span8}>{t.symbol}</Text>
                              </View>
                            </View>
                            <View style={styles.col}>
                              <View style={styles.rowCenter}>
                                <Text style={styles.span8}>
                                  {getAccountStatementActionDescription(
                                    t.action
                                  )}
                                </Text>
                              </View>
                            </View>
                            <View style={styles.colPositions}>
                              <View style={styles.rowCenter}>
                                <Text style={styles.span8}>{t.quantity}</Text>
                              </View>
                            </View>
                            <View style={styles.col}>
                              <View style={styles.rowCenter}>
                                <Text style={styles.span8}>{t.entryPrice}</Text>
                              </View>
                            </View>
                            <View style={styles.col}>
                              <View style={styles.rowCenter}>
                                <Text style={styles.span8}>{t.exitPrice}</Text>
                              </View>
                            </View>
                            <View style={styles.col}>
                              <View style={styles.rowCenter}>
                                <Text style={styles.span8}>{t.fee}</Text>
                              </View>
                            </View>
                            <View style={styles.col}>
                              <View style={styles.rowCenter}>
                                <Text style={styles.span8}>{t.commission}</Text>
                              </View>
                            </View>
                            <View style={styles.col}>
                              <View style={styles.rowCenter}>
                                <Text style={styles.span8}>{t.swap}</Text>
                              </View>
                            </View>
                            <View style={styles.col}>
                              <View style={styles.rowCenter}>
                                <Text style={styles.span8}>{t.spread}</Text>
                              </View>
                            </View>
                            <View style={styles.col}>
                              <View style={styles.rowCenter}>
                                <Text style={styles.span8}>{t.pl}</Text>
                              </View>
                            </View>
                            <View style={styles.col}>
                              <View style={styles.rowCenter}>
                                <Text style={styles.span8}>{t.comment}</Text>
                              </View>
                            </View>
                          </View>
                        ) : (
                          <View style={styles.rowMB5}>
                            <View style={styles.colPositions}>
                              <View style={styles.row}>
                                <Text style={styles.span8}></Text>
                              </View>
                            </View>
                            <View style={styles.col}>
                              <View style={styles.rowCenter}>
                                <Text style={styles.span8Italic}>
                                  {formatAccountStatementDatetime(t.entryTime)}
                                </Text>
                              </View>
                            </View>
                            <View style={styles.col}>
                              <View style={styles.rowCenter}>
                                <Text style={styles.span8Italic}>
                                  {formatAccountStatementDatetime(t.exitTime)}
                                </Text>
                              </View>
                            </View>
                            <View style={styles.colPositions}>
                              <View style={styles.rowCenter}>
                                <Text style={styles.span8Italic}>
                                  {t.symbol}
                                </Text>
                              </View>
                            </View>
                            <View style={styles.col}>
                              <View style={styles.rowCenter}>
                                <Text style={styles.span8Italic}>
                                  {getAccountStatementActionDescription(
                                    t.action
                                  )}
                                </Text>
                              </View>
                            </View>
                            <View style={styles.colPositions}>
                              <View style={styles.row}>
                                <Text style={styles.span8}></Text>
                              </View>
                            </View>
                            <View style={styles.col}>
                              <View style={styles.row}>
                                <Text style={styles.span8}></Text>
                              </View>
                            </View>
                            <View style={styles.col}>
                              <View style={styles.row}>
                                <Text style={styles.span8}></Text>
                              </View>
                            </View>
                            <View style={styles.col}>
                              <View style={styles.rowCenter}>
                                <Text style={styles.span8Italic}>
                                  {t.spread}
                                </Text>
                              </View>
                            </View>
                            <View style={styles.col}>
                              <View style={styles.rowCenter}>
                                <Text style={styles.span8Italic}>{t.pl}</Text>
                              </View>
                            </View>
                            <View style={styles.col}>
                              <View style={styles.rowCenter}>
                                <Text style={styles.span8Italic}>
                                  {t.comment}
                                </Text>
                              </View>
                            </View>
                          </View>
                        )}
                      </View>
                    ))}
                  </View>
                ) : (
                  <Text style={styles.span}>
                    {translate('COMMON.LABELS.NO_TRANSACTIONS')}
                  </Text>
                )}
              </View>
              <View style={styles.column_MT_50}>
                <Text style={styles.h2}>
                  {translate('COMMON.LABELS.RESULTS')}
                </Text>
              </View>
              <View style={styles.column_MT_5}>
                <View style={styles.row}>
                  <View style={styles.col4}>
                    <View style={styles.column}>
                      <TableData
                        viewStyles={styles.tr}
                        labelStyles={styles.labelRight150}
                        valueStyles={styles.value}
                        label={translate('COMMON.LABELS.TOTAL_NET_PROFIT')}
                        value={formatDecimal(accountStatement.netProfit)}
                      />
                      <TableData
                        viewStyles={styles.tr}
                        labelStyles={styles.labelRight150}
                        valueStyles={styles.value}
                        label={translate('COMMON.LABELS.PROFIT_FACTOR')}
                        value={formatDecimal(accountStatement.profitFactor)}
                      />
                      <TableData
                        viewStyles={styles.tr}
                        labelStyles={styles.labelRight150}
                        valueStyles={styles.value}
                        label={translate('COMMON.LABELS.RECOVERY_FACTOR')}
                        value={translate('COMMON.LABELS.N_A')}
                      />
                      <TableData
                        viewStyles={styles.tr}
                        labelStyles={styles.labelRight150}
                        valueStyles={styles.value}
                        label={translate('COMMON.LABELS.BALANCE_DRAWDOWN')}
                        value={translate('COMMON.LABELS.N_A')}
                      />
                      <TableData
                        viewStyles={styles.tr}
                        labelStyles={styles.labelRight150}
                        valueStyles={styles.value}
                        label={translate(
                          'COMMON.LABELS.BALANCE_DRAWDOWN_ABSOLUTE'
                        )}
                        value={formatDecimal(accountStatement.absoluteDrawdown)}
                      />
                      <TableData
                        viewStyles={styles.tr}
                        labelStyles={styles.labelRight150}
                        valueStyles={styles.value}
                        label={translate('COMMON.LABELS.TOTAL_TRADES')}
                        value={accountStatement.totalTrades}
                      />
                    </View>
                  </View>
                  <View style={styles.col4}>
                    <View style={styles.column}>
                      <TableData
                        viewStyles={styles.tr}
                        labelStyles={styles.labelRight150}
                        valueStyles={styles.value}
                        label={translate('COMMON.LABELS.GROSS_PROFIT')}
                        value={formatDecimal(accountStatement.grossProfit)}
                      />
                      <TableData
                        viewStyles={styles.tr}
                        labelStyles={styles.labelRight150}
                        valueStyles={styles.value}
                        label={translate('COMMON.LABELS.EXPECTED_PAYOFF')}
                        value={formatDecimal(accountStatement.expectedPayoff)}
                      />
                      <TableData
                        viewStyles={styles.tr}
                        labelStyles={styles.labelRight150}
                        valueStyles={styles.value}
                        label={translate('COMMON.LABELS.SHARPE_RATIO')}
                        value={translate('COMMON.LABELS.N_A')}
                      />
                      <TableData
                        viewStyles={styles.tr}
                        labelStyles={styles.space}
                        valueStyles={styles.space}
                        label={'Space'}
                        value={'Space'}
                      />
                      <TableData
                        viewStyles={styles.tr}
                        labelStyles={styles.labelRight150}
                        valueStyles={styles.value}
                        label={translate(
                          'COMMON.LABELS.BALANCE_DRAWDOWN_MAXIMAL'
                        )}
                        value={`${formatDecimal(
                          accountStatement.maxDrawdown.value
                        )} (${formatDecimal(
                          accountStatement.maxDrawdown.percent
                        )}%)`}
                      />
                      <TableData
                        viewStyles={styles.tr}
                        labelStyles={styles.labelRight150}
                        valueStyles={styles.value}
                        label={translate('COMMON.LABELS.SHORT_TRADES_WON')}
                        value={`${
                          accountStatement.shortTrades.value
                        } (${formatDecimal(
                          accountStatement.shortTrades.percent
                        )}%)`}
                      />
                      <TableData
                        viewStyles={styles.tr}
                        labelStyles={styles.labelRight150}
                        valueStyles={styles.value}
                        label={translate(
                          'COMMON.LABELS.PROFIT_TRADES_OF_TOTAL'
                        )}
                        value={`${
                          accountStatement.profitTrades.value
                        } (${formatDecimal(
                          accountStatement.profitTrades.percent
                        )}%)`}
                      />
                      <TableData
                        viewStyles={styles.tr}
                        labelStyles={styles.labelRight150}
                        valueStyles={styles.value}
                        label={translate('COMMON.LABELS.LARGEST_PROFIT_TRADE')}
                        value={accountStatement.largestTrade}
                      />
                      <TableData
                        viewStyles={styles.tr}
                        labelStyles={styles.labelRight150}
                        valueStyles={styles.value}
                        label={translate('COMMON.LABELS.AVERAGE_PROFIT_TRADE')}
                        value={accountStatement.avgProfitTrade}
                      />
                      <TableData
                        viewStyles={styles.tr}
                        labelStyles={styles.labelRight150}
                        valueStyles={styles.value}
                        label={translate(
                          'COMMON.LABELS.MAXIMUM_CONSECUTIVE_WINS',
                          {
                            currency: currency,
                          }
                        )}
                        value={`${
                          accountStatement.maxConsecutiveProfit
                        } (${formatDecimal(
                          accountStatement.maxConsecutiveWins
                        )})`}
                      />
                      <TableData
                        viewStyles={styles.tr}
                        labelStyles={styles.labelRight150}
                        valueStyles={styles.value}
                        label={translate(
                          'COMMON.LABELS.MAXIMAL_CONSECUTIVE_WIN_COUNT'
                        )}
                        value={`${
                          accountStatement.maxConsecutiveWins
                        } (${formatDecimal(
                          accountStatement.maxConsecutiveProfit
                        )})`}
                      />
                      <TableData
                        viewStyles={styles.tr}
                        labelStyles={styles.labelRight150}
                        valueStyles={styles.value}
                        label={translate(
                          'COMMON.LABELS.AVERAGE_CONSECUTIVE_WINS'
                        )}
                        value={accountStatement.avgConsecutiveWins}
                      />
                    </View>
                  </View>
                  <View style={styles.col4}>
                    <View style={styles.column}>
                      <TableData
                        viewStyles={styles.tr}
                        labelStyles={styles.labelRight150}
                        valueStyles={styles.value}
                        label={translate('COMMON.LABELS.GROSS_LOSS')}
                        value={formatDecimal(accountStatement.grossLoss)}
                      />
                      <TableData
                        viewStyles={styles.tr}
                        labelStyles={styles.space}
                        valueStyles={styles.space}
                        label={'Space'}
                        value={'Space'}
                      />
                      <TableData
                        viewStyles={styles.tr}
                        labelStyles={styles.space}
                        valueStyles={styles.space}
                        label={'Space'}
                        value={'Space'}
                      />
                      <TableData
                        viewStyles={styles.tr}
                        labelStyles={styles.space}
                        valueStyles={styles.space}
                        label={'Space'}
                        value={'Space'}
                      />
                      <TableData
                        viewStyles={styles.tr}
                        labelStyles={styles.labelRight150}
                        valueStyles={styles.value}
                        label={translate(
                          'COMMON.LABELS.BALANCE_DRAWDOWN_RELATIVE'
                        )}
                        value={`${formatDecimal(
                          accountStatement.relativeDrawdown.value
                        )} (${formatDecimal(
                          accountStatement.relativeDrawdown.percent
                        )}%)`}
                      />
                      <TableData
                        viewStyles={styles.tr}
                        labelStyles={styles.labelRight150}
                        valueStyles={styles.value}
                        label={translate('COMMON.LABELS.LONG_TRADES_WON')}
                        value={`${formatDecimal(
                          accountStatement.longTrades.value
                        )} (${formatDecimal(
                          accountStatement.longTrades.percent
                        )}%)`}
                      />
                      <TableData
                        viewStyles={styles.tr}
                        labelStyles={styles.labelRight150}
                        valueStyles={styles.value}
                        label={translate('COMMON.LABELS.LOSS_TRADES_OF_TOTAL')}
                        value={`${formatDecimal(
                          accountStatement.lossTrades.value
                        )} (${formatDecimal(
                          accountStatement.lossTrades.percent
                        )}%)`}
                      />
                      <TableData
                        viewStyles={styles.tr}
                        labelStyles={styles.labelRight150}
                        valueStyles={styles.value}
                        label={translate('COMMON.LABELS.LARGEST_LOSS_TRADE')}
                        value={formatDecimal(accountStatement.largestLossTrade)}
                      />
                      <TableData
                        viewStyles={styles.tr}
                        labelStyles={styles.labelRight150}
                        valueStyles={styles.value}
                        label={translate('COMMON.LABELS.AVERAGE_LOSS_TRADE')}
                        value={formatDecimal(accountStatement.avgLossTrade)}
                      />
                      <TableData
                        viewStyles={styles.tr}
                        labelStyles={styles.labelRight150}
                        valueStyles={styles.value}
                        label={translate(
                          'COMMON.LABELS.MAXIMUM_CONSECUTIVE_LOSSES',
                          {
                            currency: currency,
                          }
                        )}
                        value={`${formatDecimal(
                          accountStatement.maxConsecutiveLoss
                        )} (${formatDecimal(
                          accountStatement.maxConsecutiveLosses
                        )})`}
                      />
                      <TableData
                        viewStyles={styles.tr}
                        labelStyles={styles.labelRight150}
                        valueStyles={styles.value}
                        label={translate(
                          'COMMON.LABELS.MAXIMUM_CONSECUTIVE_LOSSES_COUNT'
                        )}
                        value={`${formatDecimal(
                          accountStatement.maxConsecutiveLosses
                        )} (${formatDecimal(
                          accountStatement.maxConsecutiveLoss
                        )})`}
                      />
                      <TableData
                        viewStyles={styles.tr}
                        labelStyles={styles.labelRight150}
                        valueStyles={styles.value}
                        label={translate(
                          'COMMON.LABELS.AVERAGE_CONSECUTIVE_LOSSES'
                        )}
                        value={accountStatement.avgConsecutiveLosses}
                      />
                    </View>
                  </View>
                </View>
              </View>
            </View>
          </View>
        </Page>
      </Document>
    );
  }
);
